import React, { FunctionComponent } from 'react';
import { ApolloError } from 'apollo-boost';
import { mergeAll, pathOr, propOr } from 'ramda';
import { Grid, Statistic } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { FormattedDate } from 'react-intl';

import Query from 'components/Query';
import { MerchantStatsPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';

import styles from './StatisticSection.styles';
import GET_VENUE_STATISTICS from './StatisticSection.queries';

type TransactionCounts = {
  AMEX: number | string;
  MASTERCARD: number | string;
  VISA: number | string;
  total: number | string;
};

type StatisticData = {
  transactionTimestamp: Date;
  averageTransactionAmount: number;
} & TransactionCounts;

type Params = {
  startDate: string;
  endDate: string;
  venueUuid: string;
  merchantId: string;
};

type ResponseData = {
  averageTransactionAmountForVenue: number;
  cnTransactionCountsForVenueByMarketplace: TransactionCounts;
  lastTransactionDate: Date;
  loading?: boolean;
  error?: ApolloError;
};

type Props = {
  variables: { params: Params };
  classes: ClassProp<typeof styles>;
};

const renderLastTransaction = (lastTransaction: Date) => {
  if (lastTransaction) {
    return (
      <FormattedDate value={lastTransaction} month="short" day="2-digit" />
    );
  }

  return 'N/A';
};

const renderStat = (
  label: string,
  value: any,
  classes: ClassProp<typeof styles>,
) => (
  <Grid.Column>
    <Statistic size="tiny">
      <Statistic.Label className={classes.label}>{label}</Statistic.Label>
      <Statistic.Value className={classes.value}>{value}</Statistic.Value>
    </Statistic>
  </Grid.Column>
);

const renderStatisticSection = (classes: ClassProp<typeof styles>) => (
  response: ResponseData,
) => {
  const {
    averageTransactionAmountForVenue,
    cnTransactionCountsForVenueByMarketplace,
    lastTransactionDate,
  } = pathOr({}, ['data'], response);

  const cnTransactionCountsForVenue = propOr(
    {},
    'DOSH',
    cnTransactionCountsForVenueByMarketplace,
  );

  const statisticData = mergeAll([
    averageTransactionAmountForVenue,
    cnTransactionCountsForVenue,
    lastTransactionDate,
  ]) as StatisticData;

  const {
    transactionTimestamp,
    averageTransactionAmount,
    AMEX = 'N/A',
    MASTERCARD = 'N/A',
    VISA = 'N/A',
    total = 'N/A',
  } = statisticData;

  if (response.loading) {
    return <MerchantStatsPlaceholder />;
  }

  return (
    <Grid className={classes.container}>
      <Grid.Row columns="equal">
        {renderStat('Total TRXNS', total, classes)}
        {renderStat('VISA', VISA, classes)}
        {renderStat('MASTERCARD', MASTERCARD, classes)}
        {renderStat('AMEX', AMEX, classes)}
        {renderStat(
          'AVG. TRANSACTION',
          `$${averageTransactionAmount}`,
          classes,
        )}
        {renderStat(
          'LAST TRANSACTION',
          renderLastTransaction(transactionTimestamp),
          classes,
        )}
      </Grid.Row>
    </Grid>
  );
};

const StatisticSection: FunctionComponent<Props> = ({ variables, classes }) => (
  <Query
    enableProgressInfo
    variables={variables}
    query={GET_VENUE_STATISTICS}
    render={renderStatisticSection(classes)}
  />
);

export default withStyles(styles)(StatisticSection);
